/**
 * Cliente da API (`/api/v1`). Mesma origem do backend: o cookie de sessão vai junto
 * e o CSRF segue o padrão do Django (cookie `csrftoken` + header `X-CSRFToken`).
 */

export const BASE_API = '/api/v1';

export interface Meta {
  gerado_em: string;
  dados_ate: string | null;
  cache: 'hit' | 'miss';
  avisos: string[];
}

export interface Envelope<T> {
  meta: Meta;
  data: T;
}

export class ErroApi extends Error {
  readonly status: number;
  readonly campo?: string;

  constructor(status: number, mensagem: string, campo?: string) {
    super(mensagem);
    this.name = 'ErroApi';
    this.status = status;
    this.campo = campo;
  }
}

export type ValorParametro = string | number | boolean | null | undefined;
/** Listas viram parâmetros repetidos (?ativos=A&ativos=B), como o django-ninja espera. */
export type Parametros = Record<string, ValorParametro | ValorParametro[]>;

export function montarUrl(caminho: string, parametros: Parametros = {}): string {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(parametros)) {
    for (const item of Array.isArray(valor) ? valor : [valor]) {
      if (item !== undefined && item !== null && item !== '') busca.append(chave, String(item));
    }
  }
  const qs = busca.toString();
  return `${BASE_API}${caminho}${qs ? `?${qs}` : ''}`;
}

async function erroDaResposta(resposta: Response): Promise<ErroApi> {
  let mensagem = `Erro ${resposta.status}`;
  let campo: string | undefined;
  try {
    const corpo = (await resposta.json()) as { detail?: unknown; campo?: string };
    if (typeof corpo.detail === 'string') mensagem = corpo.detail;
    else if (Array.isArray(corpo.detail)) mensagem = 'Parâmetros inválidos.';
    campo = corpo.campo;
  } catch {
    // corpo não-JSON: mantém a mensagem genérica
  }
  return new ErroApi(resposta.status, mensagem, campo);
}

export async function apiGet<T>(
  caminho: string,
  parametros?: Parametros,
  sinal?: AbortSignal,
): Promise<T> {
  const resposta = await fetch(montarUrl(caminho, parametros), {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal: sinal,
  });
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return (await resposta.json()) as T;
}

/**
 * POST de leitura (corpo JSON), para entradas que não cabem na URL (ex.: tabela colada).
 * Envia o token CSRF, exigido pela autenticação por sessão.
 */
export async function apiPost<T>(caminho: string, corpo: unknown, sinal?: AbortSignal): Promise<T> {
  const resposta = await fetch(montarUrl(caminho), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRFToken': await obterCsrf(),
    },
    body: JSON.stringify(corpo),
    signal: sinal,
  });
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return (await resposta.json()) as T;
}

/** POST multipart (upload de arquivo), com o token CSRF. Só leitura no servidor. */
export async function apiPostForm<T>(
  caminho: string,
  dados: FormData,
  sinal?: AbortSignal,
): Promise<T> {
  const resposta = await fetch(montarUrl(caminho), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'X-CSRFToken': await obterCsrf() },
    body: dados,
    signal: sinal,
  });
  if (!resposta.ok) throw await erroDaResposta(resposta);
  return (await resposta.json()) as T;
}

/**
 * POST multipart que devolve um arquivo (ex.: basket em Excel gerado a partir de um
 * upload): baixa no navegador com o nome do `Content-Disposition`.
 */
export async function apiPostFormBaixar(caminho: string, dados: FormData): Promise<void> {
  const resposta = await fetch(montarUrl(caminho), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-CSRFToken': await obterCsrf() },
    body: dados,
  });
  if (!resposta.ok) throw await erroDaResposta(resposta);
  const nome =
    /filename="([^"]+)"/.exec(resposta.headers.get('Content-Disposition') ?? '')?.[1] ?? 'arquivo';
  const url = URL.createObjectURL(await resposta.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

/** Token CSRF: lê o cookie; se não houver, pede ao backend (que grava o cookie). */
export async function obterCsrf(): Promise<string> {
  const doCookie = document.cookie
    .split('; ')
    .find((c) => c.startsWith('csrftoken='))
    ?.split('=')[1];
  if (doCookie) return decodeURIComponent(doCookie);
  const { csrf_token } = await apiGet<{ csrf_token: string }>('/csrf');
  return csrf_token;
}
